import { DateTime } from "luxon";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import "./InfoCard.css";

// Simple input component with boxed letters
const UnderlinedInput = ({
  value,
  onChange,
  name,
  length,
  disabled = false,
  inputMode,
  pattern,
  onBackspaceAtStart,
}) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    let newValue = e.target.value;
    if (inputMode === "numeric") {
      newValue = newValue.replace(/[^0-9]/g, "");
    }
    newValue = newValue.slice(0, length);
    onChange({ target: { name, value: newValue } });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const el = inputRef.current;
      const caretAtStart = !el || (el.selectionStart === 0 && el.selectionEnd === 0);
      if ((value || '').length === 0 || caretAtStart) {
        if (typeof onBackspaceAtStart === 'function') {
          e.preventDefault();
          onBackspaceAtStart();
          return;
        }
      }
    }
  };

  const focusInput = () => {
    if (disabled) return;
    const el = inputRef.current;
    if (el) {
      el.focus();
      try {
        const pos = (value || "").length;
        el.setSelectionRange(pos, pos);
      } catch (_) {
        // selection may fail on some inputModes; safe to ignore
      }
    }
  };

  const renderLetters = () => {
    const letters = value.split("");
    const remainingLength = Math.max(0, length - letters.length);
    const spaces = remainingLength > 0 ? Array(remainingLength).fill(" ") : [];

    return [...letters, ...spaces].map((letter, index) => (
      <span key={index} className="letter-box">
        {letter}
        <span className="letter-box-border"></span>
      </span>
    ));
  };

  return (
    <div className="input-container" onClick={focusInput} style={{ cursor: disabled ? "default" : "text" }}>
      {!disabled && (
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          name={name}
          className="hidden-input"
          maxLength={length}
          inputMode={inputMode}
          pattern={pattern}
          ref={inputRef}
        />
      )}
      <div className="letter-display" onClick={focusInput}>{renderLetters()}</div>
    </div>
  );
};

// CheckboxBox component for custom checkboxes
const CheckboxBox = ({ checked, onClick, disabled, size = 22, style = {} }) => (
  <div
    onClick={disabled ? undefined : onClick}
    style={{
      width: size,
      height: size,
      border: `2px solid #8b0000`,
      borderRadius: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "transparent",
      color: "#8b0000",
      fontSize: size * 0.95,
      fontWeight: "bold",
      userSelect: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      ...style,
    }}
  >
    {checked ? "✕" : ""}
  </div>
);

const InfoCard = ({ onContinue, onShowCitySelector, initialData, isPartnerForm = false }, ref) => {
  // Local state for birth chart data
  const [infoCardData, setInfoCardData] = useState({
    name: "",
    birthDate: null,
    birthLocation: null,
    unknownTime: false,
    gender: "F",
  });

  // Responsive input lengths based on screen size
  const getResponsiveLength = (fieldName) => {
    const isTablet = window.innerWidth >= 768;
    const isDesktop = window.innerWidth >= 1024;
    const isVeryShortScreen = window.innerHeight <= 600; // Nest Hub and similar devices

    if (isDesktop) {
      // Desktop: longer inputs
      switch (fieldName) {
        case 'name': return 20;
        case 'birthLocation': return 20;
        default: return 12;
      }
    } else if (isTablet) {
      // Tablet: medium inputs
      switch (fieldName) {
        case 'name': return 16;
        case 'birthLocation': return 16;
        default: return 12;
      }
    } else {
      // Mobile: current lengths
      switch (fieldName) {
        case 'name': return 12;
        case 'birthLocation': return 12;
        default: return 12;
      }
    }
  };

  // Get compact spacing for very short screens
  const getCompactSpacing = () => {
    const isVeryShortScreen = window.innerHeight <= 600;
    return {
      containerPadding: isVeryShortScreen ? 15 : 20,
      fieldMargin: isVeryShortScreen ? 6 : 8,
      fieldPadding: isVeryShortScreen ? 4 : 6,
      fieldGap: isVeryShortScreen ? 2 : 3,
      headerMargin: isVeryShortScreen ? 15 : 20,
      headerPadding: isVeryShortScreen ? 8 : 10,
      titleSize: isVeryShortScreen ? 20 : 22,
      labelSize: isVeryShortScreen ? 12 : 13,
      buttonMargin: isVeryShortScreen ? 4 : 6,
      buttonPadding: isVeryShortScreen ? 8 : 10,
    };
  };

  // State to track screen size for responsive updates
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Handle window resize for responsive updates
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to load data from localStorage
  const loadDataFromLocalStorage = () => {
    try {
      // Use different localStorage keys for person 1 vs person 2
      const storageKey = isPartnerForm ? 'wooju_partner_profile_data' : 'wooju_profile_data';
      const savedData = localStorage.getItem(storageKey);

      if (savedData) {
        const parsedData = JSON.parse(savedData);

        // Convert ISO string back to DateTime and ensure all fields are present
        const birthChartData = {
          name: parsedData.name || "",
          birthDate: parsedData.birthDate ? DateTime.fromISO(parsedData.birthDate, { setZone: true }) : null,
          birthLocation: parsedData.birthLocation || null,
          unknownTime: parsedData.unknownTime || false,
          gender: parsedData.gender || "F",
        };

        setInfoCardData(birthChartData);

        // Load individual form fields exactly as they were saved
        if (parsedData.year) {
          setYear(parsedData.year);
          setMonth(parsedData.month || "");
          setDay(parsedData.day || "");
          setHour(parsedData.hour || "");
          setMinute(parsedData.minute || "");
          setAmPm(parsedData.ampm || "AM");
        } else {
          // Clear date/time fields if no saved data
          setYear("");
          setMonth("");
          setDay("");
          setHour("");
          setMinute("");
          setAmPm("AM");
        }
      } else {
        setInfoCardData({
          name: "",
          birthDate: null,
          birthLocation: null,
          unknownTime: false,
          gender: "F",
        });

        // Clear date/time fields
        setYear("");
        setMonth("");
        setDay("");
        setHour("");
        setMinute("");
        setAmPm("AM");
      }
    } catch (error) {
      // Silent fail - clear data if corrupted
      setInfoCardData({
        name: "",
        birthDate: null,
        birthLocation: null,
        unknownTime: false,
        gender: "F",
      });
      setYear("");
      setMonth("");
      setDay("");
      setHour("");
      setMinute("");
      setAmPm("AM");
    }
  };

  // Load data from localStorage on component mount
  useEffect(() => {
    loadDataFromLocalStorage();
  }, []);

  // Local state for date/time fields
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [ampm, setAmPm] = useState("AM");

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Comprehensive validation function
  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!infoCardData.name || infoCardData.name.trim().length === 0) {
      errors.name = "Name is required";
    } else if (infoCardData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    }

    // Date validation
    if (!year || !month || !day) {
      errors.date = "Complete birth date is required";
    } else {
      const y = parseInt(year);
      const m = parseInt(month);
      const d = parseInt(day);

      // Year validation
      if (y < 1900 || y > new Date().getFullYear()) {
        errors.date = "Year must be between 1900 and " + new Date().getFullYear();
      }

      // Month validation
      if (m < 1 || m > 12) {
        errors.date = "Month must be between 01 and 12";
      }

      // Day validation
      if (d < 1 || d > 31) {
        errors.date = "Day must be between 01 and 31";
      }

      // Check if date is valid (handles leap years, month lengths, etc.)
      if (!errors.date) {
        const date = new Date(y, m - 1, d);
        const isValidDate = date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
        if (!isValidDate) {
          errors.date = "Invalid date (e.g., February 30th doesn't exist)";
        }
      }
    }

    // Time validation (only if not unknown)
    if (!infoCardData.unknownTime) {
      if (hour === "" || minute === "") {
        errors.time = "Complete birth time is required (or check 'UNKNOWN')";
      } else {
        const h = parseInt(hour);
        const min = parseInt(minute);

        // Hour validation (12-hour format)
        if (h < 1 || h > 12) {
          errors.time = "Hour must be between 01 and 12";
        }

        // Minute validation
        if (min < 0 || min > 59) {
          errors.time = "Minutes must be between 00 and 59";
        }

        // Check for valid time format
        if (!errors.time && (hour.length !== 2 || minute.length !== 2)) {
          errors.time = "Time must be in HH:MM format (e.g., 04:00)";
        }
      }
    }

    // Location validation
    if (!infoCardData.birthLocation) {
      errors.location = "Birth location is required";
    }

    return errors;
  };

  // Check if date/time is valid (for internal use)
  const isValidDateTime = () => {
    const y = parseInt(year) || 0;
    const m = parseInt(month) || 0;
    const d = parseInt(day) || 0;

    // Safety check for valid date components
    if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) {
      return false;
    }

    const date = new Date(y, m - 1, d); // JS months are 0-11
    const isValidDate =
      date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;

    if (!isValidDate) return false;

    // Time validation - empty time is valid
    if (hour === "" && minute === "") return true;

    // If either hour or minute is set, both must be valid
    if (hour === "" || minute === "") return false;

    const h = parseInt(hour) || 0;
    const min = parseInt(minute) || 0;

    return h >= 0 && h <= 12 && min >= 0 && min <= 59;
  };

  // Initialize local state from store's birthDate
  useEffect(() => {
    if (
      infoCardData.birthDate &&
      typeof infoCardData.birthDate === "object" &&
      typeof infoCardData.birthDate.toISO === "function"
    ) {
      // Use timezone from birthLocation if available, else UTC
      let tz = "UTC";
      if (infoCardData.birthLocation && infoCardData.birthLocation.timezone) {
        tz = infoCardData.birthLocation.timezone;
      }
      // Convert to correct zone for display
      const dt = infoCardData.birthDate.setZone(tz, { keepLocalTime: false });
      setYear(String(dt.year));
      setMonth(String(dt.month).padStart(2, "0"));
      setDay(String(dt.day).padStart(2, "0"));
      let h = dt.hour;
      setAmPm(h >= 12 ? "PM" : "AM");
      h = h % 12 === 0 ? 12 : h % 12;
      setHour(String(h).padStart(2, "0"));
      setMinute(String(dt.minute).padStart(2, "0"));
    }
  }, []);

  // When any date/time field changes, update infoCardData.birthDate if all required fields are set
  useEffect(() => {
    if (isValidDateTime()) {
      let tz = "UTC";
      if (infoCardData.birthLocation && infoCardData.birthLocation.timezone) {
        tz = infoCardData.birthLocation.timezone;
      }

      // Add safety checks for parseInt operations
      const yearNum = parseInt(year) || 0;
      const monthNum = parseInt(month) || 0;
      const dayNum = parseInt(day) || 0;
      let h = hour ? (parseInt(hour) || 0) : 0;
      let min = minute ? (parseInt(minute) || 0) : 0;

      if (ampm === "PM") {
        if (h !== 12) {
          h += 12;
        }
      } else {
        if (h === 12) {
          h = 0;
        }
      }
      // If unknownTime, set hour/minute to 0
      if (infoCardData.unknownTime) {
        h = 0;
        min = 0;
      }
      // Construct DateTime in the correct zone
      const dt = DateTime.fromObject(
        {
          year: yearNum,
          month: monthNum,
          day: dayNum,
          hour: h,
          minute: min,
          second: 0,
        },
        { zone: tz }
      );
      setInfoCardData(prev => ({
        ...prev,
        birthDate: dt,
      }));
    }
  }, [
    infoCardData.birthLocation,
    infoCardData.unknownTime,
    year,
    month,
    day,
    hour,
    minute,
    ampm,
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInfoCardData({
      ...infoCardData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear validation errors when user starts typing
    if (showValidationErrors) {
      const newErrors = { ...validationErrors };
      delete newErrors[name];
      setValidationErrors(newErrors);

      // If no errors left, hide validation display
      if (Object.keys(newErrors).length === 0) {
        setShowValidationErrors(false);
      }
    }
  };

  // Function to save data to localStorage
  const saveToLocalStorage = (data) => {
    try {
      // Parse the birth date components
      let birthDate = null;
      if (data.year && data.month && data.day) {
        const yearNum = parseInt(data.year);
        const monthNum = parseInt(data.month);
        const dayNum = parseInt(data.day);
        let h = parseInt(data.hour) || 0;
        let min = parseInt(data.minute) || 0;
        let tz = "UTC";

        // Convert 12-hour format to 24-hour format
        if (data.ampm === "PM" && h !== 12) {
          h += 12;
        } else if (data.ampm === "AM" && h === 12) {
          h = 0;
        }

        // If unknownTime, set hour/minute to 0
        if (data.unknownTime) {
          h = 0;
          min = 0;
        }

        birthDate = DateTime.fromObject(
          {
            year: yearNum,
            month: monthNum,
            day: dayNum,
            hour: h,
            minute: min,
            second: 0,
          },
          { zone: tz }
        );
      }

      // Convert DateTime to ISO string for localStorage storage
      const dataToSave = {
        name: data.name || "",
        birthDate: birthDate ? birthDate.toISO() : null,
        // Store individual form fields exactly as entered
        year: year || "",
        month: month || "",
        day: day || "",
        hour: hour || "",
        minute: minute || "",
        ampm: ampm || "AM",
        birthLocation: data.birthLocation || null,
        unknownTime: data.unknownTime || false,
        gender: data.gender || "F",
        savedAt: new Date().toISOString()
      };

      // Save to localStorage with appropriate key
      const storageKey = isPartnerForm ? 'wooju_partner_profile_data' : 'wooju_profile_data';
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      console.log(`${isPartnerForm ? 'Partner' : 'Profile'} saved to localStorage`);

    } catch (error) {
      console.error('Error saving to localStorage:', error);
      // Silent fail
    }
  };

  const handleContinue = async () => {
    // Validate form before proceeding
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowValidationErrors(true);

      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }

      return; // Don't proceed if there are errors
    }

    // Clear any previous errors
    setValidationErrors({});
    setShowValidationErrors(false);

    // Save to localStorage when user completes the form
    saveToLocalStorage(infoCardData);
    // Then call the parent's onContinue
    onContinue(infoCardData);
  };

  // Function to update city directly (called from parent)
  const updateCity = (city) => {
    setInfoCardData(prev => {
      const updated = {
        ...prev,
        birthLocation: city
      };
      return updated;
    });
  };

  useImperativeHandle(ref, () => ({
    updateCity: updateCity,
  }));

  // Auto-save profile data as user types (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Only save if we have some data to save
      if (infoCardData.name || year || month || day || hour || minute) {
        const dataToSave = {
          name: infoCardData.name || "",
          birthLocation: infoCardData.birthLocation || null,
          unknownTime: infoCardData.unknownTime || false,
          gender: infoCardData.gender || "F",
          year: year || "",
          month: month || "",
          day: day || "",
          hour: hour || "",
          minute: minute || "",
          ampm: ampm || "AM",
        };
        saveToLocalStorage(dataToSave);
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [infoCardData.name, infoCardData.birthLocation, infoCardData.unknownTime, infoCardData.gender, year, month, day, hour, minute, ampm]);

  const handleSetAmPm = (next) => {
    // If UNKNOWN is set, clear it first, then set AM/PM
    if (infoCardData.unknownTime) {
      handleChange({
        target: {
          name: "unknownTime",
          type: "checkbox",
          checked: false,
        },
      });
    }
    setAmPm(next);
    // Optionally bring focus to hour field for quick entry
    const hourEl = document.querySelector("input[name='birthHour']");
    if (hourEl) hourEl.focus();
  };

  return (
    <div
      style={{
        maxWidth: 600,
        margin: `calc(var(--safe-area-top) + ${getCompactSpacing().containerPadding - 5}px) auto calc(var(--safe-area-bottom) + ${getCompactSpacing().containerPadding - 5}px) auto`,
        padding: getCompactSpacing().containerPadding,
        background: `
          linear-gradient(0deg, rgba(80, 120, 200, 0.07), rgba(80, 120, 200, 0.07)),
          linear-gradient(135deg, #f4f1e8 0%, #e8dcc0 50%, #d4c4a8 100%),
          radial-gradient(circle at 25% 25%, rgba(139, 69, 19, 0.4) 0%, transparent 40%),
          radial-gradient(circle at 75% 75%, rgba(160, 82, 45, 0.3) 0%, transparent 40%),
          radial-gradient(circle at 10% 80%, rgba(101, 67, 33, 0.25) 0%, transparent 35%),
          radial-gradient(circle at 90% 20%, rgba(139, 69, 19, 0.2) 0%, transparent 30%)
        `,
        border: "3px solid rgba(139, 0, 0, 0.8)",
        borderRadius: 8,
        boxShadow: `
          0 4px 8px rgba(0, 0, 0, 0.3),
          inset 0 0 50px rgba(139, 69, 19, 0.15),
          0 2px 4px rgba(0, 0, 0, 0.2)
        `,
        fontFamily: "Xanh Mono, monospace",
        color: "#8b0000",
        position: "relative",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      {/* Fold lines overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(to bottom, transparent 0%, transparent 20%, rgba(139, 69, 19, 0.1) 25%, transparent 30%, transparent 70%, rgba(139, 69, 19, 0.08) 75%, transparent 80%, transparent 100%),
            linear-gradient(to bottom, transparent 0%, transparent 40%, rgba(139, 69, 19, 0.06) 45%, transparent 50%, transparent 100%)
          `,
          pointerEvents: "none",
          borderRadius: 5,
          zIndex: 1,
        }}
      />
      <div
        style={{
          textAlign: "center",
          marginBottom: getCompactSpacing().headerMargin,
          borderBottom: "2px solid #8b0000",
          paddingBottom: getCompactSpacing().headerPadding,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: getCompactSpacing().titleSize,
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: 2,
            color: "#8b0000",
          }}
        >
          INFORMATION CARD
        </h1>
        <div style={{ fontSize: 11, marginTop: 3, fontWeight: "bold" }}>
          FORM NO. 1234
        </div>
      </div>
      <div
        style={{
          marginBottom: getCompactSpacing().fieldMargin,
          border: `2px solid ${validationErrors.name ? "#dc3545" : "#8b0000"}`,
          padding: getCompactSpacing().fieldPadding,
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: 4,
        }}
        data-field="name"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: getCompactSpacing().fieldGap }}>
          <div
            style={{
              fontWeight: "bold",
              fontSize: getCompactSpacing().labelSize,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: validationErrors.name ? "#dc3545" : "#8b0000",
              textAlign: "left",
            }}
          >
            NAME
          </div>
          <UnderlinedInput
            value={infoCardData.name}
            onChange={handleChange}
            name="name"
            length={getResponsiveLength('name')}
          />
          {showValidationErrors && validationErrors.name && (
            <div
              style={{
                color: "#dc3545",
                fontSize: 11,
                fontWeight: "bold",
                marginTop: 4,
                textAlign: "left",
              }}
            >
              ⚠️ {validationErrors.name}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          marginBottom: getCompactSpacing().fieldMargin,
          border: `2px solid ${validationErrors.date ? "#dc3545" : "#8b0000"}`,
          padding: getCompactSpacing().fieldPadding,
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: 4,
        }}
        data-field="date"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: getCompactSpacing().fieldGap }}>
          <div
            style={{
              fontWeight: "bold",
              fontSize: getCompactSpacing().labelSize,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: validationErrors.date ? "#dc3545" : "#8b0000",
              textAlign: "left",
            }}
          >
            DATE OF BIRTH (MM/DD/YYYY)
          </div>
          <div
            style={{ display: "flex", alignItems: "center", gap: 4, cursor: "text" }}
            onClick={(e) => {
              // focus first incomplete field, else year
              const monthEl = document.querySelector("input[name='birthMonth']");
              const dayEl = document.querySelector("input[name='birthDay']");
              const yearEl = document.querySelector("input[name='birthYear']");
              const target =
                (month || '').length < 2 ? monthEl :
                  (day || '').length < 2 ? dayEl :
                    yearEl;
              if (target) target.focus();
            }}
          >
            <UnderlinedInput
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                if (e.target.value.length === 2) {
                  document.querySelector("input[name='birthDay']").focus();
                }
                // Clear date validation error when user types
                if (showValidationErrors && validationErrors.date) {
                  const newErrors = { ...validationErrors };
                  delete newErrors.date;
                  setValidationErrors(newErrors);
                  if (Object.keys(newErrors).length === 0) {
                    setShowValidationErrors(false);
                  }
                }
              }}
              name="birthMonth"
              length={2}
              inputMode="numeric"
              pattern="[0-9]*"
              onBackspaceAtStart={() => {
                // no previous; stay
              }}
            />
            <span
              style={{ fontWeight: "bold", fontSize: 16, color: "#8b0000" }}
            >
              /
            </span>
            <UnderlinedInput
              value={day}
              onChange={(e) => {
                setDay(e.target.value);
                if (e.target.value.length === 2) {
                  document.querySelector("input[name='birthYear']").focus();
                }
                // Clear date validation error when user types
                if (showValidationErrors && validationErrors.date) {
                  const newErrors = { ...validationErrors };
                  delete newErrors.date;
                  setValidationErrors(newErrors);
                  if (Object.keys(newErrors).length === 0) {
                    setShowValidationErrors(false);
                  }
                }
              }}
              name="birthDay"
              length={2}
              inputMode="numeric"
              pattern="[0-9]*"
              onBackspaceAtStart={() => {
                const prev = document.querySelector("input[name='birthMonth']");
                if (prev) prev.focus();
              }}
            />
            <span
              style={{ fontWeight: "bold", fontSize: 16, color: "#8b0000" }}
            >
              /
            </span>
            <UnderlinedInput
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                if (e.target.value.length === 4) {
                  document.querySelector("input[name='birthHour']").focus();
                }
                // Clear date validation error when user types
                if (showValidationErrors && validationErrors.date) {
                  const newErrors = { ...validationErrors };
                  delete newErrors.date;
                  setValidationErrors(newErrors);
                  if (Object.keys(newErrors).length === 0) {
                    setShowValidationErrors(false);
                  }
                }
              }}
              name="birthYear"
              length={4}
              inputMode="numeric"
              pattern="[0-9]*"
              onBackspaceAtStart={() => {
                const prev = document.querySelector("input[name='birthDay']");
                if (prev) prev.focus();
              }}
            />
          </div>
          {showValidationErrors && validationErrors.date && (
            <div
              style={{
                color: "#dc3545",
                fontSize: 11,
                fontWeight: "bold",
                marginTop: 4,
                textAlign: "left",
              }}
            >
              ⚠️ {validationErrors.date}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          marginBottom: getCompactSpacing().fieldMargin,
          border: `2px solid ${validationErrors.time ? "#dc3545" : "#8b0000"}`,
          padding: getCompactSpacing().fieldPadding,
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: 4,
        }}
        data-field="time"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: getCompactSpacing().fieldGap }}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: getCompactSpacing().labelSize,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: validationErrors.time ? "#dc3545" : "#8b0000",
                  textAlign: "left",
                }}
              >
                TIME OF BIRTH
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: 6, cursor: infoCardData.unknownTime ? "default" : "text" }}
                onClick={() => {
                  if (infoCardData.unknownTime) return;
                  const hourEl = document.querySelector("input[name='birthHour']");
                  const minuteEl = document.querySelector("input[name='birthMinute']");
                  const target = (hour || '').length < 2 ? hourEl : minuteEl;
                  if (target) target.focus();
                }}
              >
                <UnderlinedInput
                  value={hour}
                  onChange={(e) => {
                    setHour(e.target.value);
                    if (e.target.value.length === 2) {
                      document
                        .querySelector("input[name='birthMinute']")
                        .focus();
                    }
                    // Clear time validation error when user types
                    if (showValidationErrors && validationErrors.time) {
                      const newErrors = { ...validationErrors };
                      delete newErrors.time;
                      setValidationErrors(newErrors);
                      if (Object.keys(newErrors).length === 0) {
                        setShowValidationErrors(false);
                      }
                    }
                  }}
                  name="birthHour"
                  length={2}
                  disabled={infoCardData.unknownTime}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onBackspaceAtStart={() => {
                    const prev = document.querySelector("input[name='birthYear']");
                    if (prev) prev.focus();
                  }}
                />
                <span
                  style={{ fontWeight: "bold", fontSize: 16, color: "#8b0000" }}
                >
                  :
                </span>
                <UnderlinedInput
                  value={minute}
                  onChange={(e) => {
                    setMinute(e.target.value);
                    // Clear time validation error when user types
                    if (showValidationErrors && validationErrors.time) {
                      const newErrors = { ...validationErrors };
                      delete newErrors.time;
                      setValidationErrors(newErrors);
                      if (Object.keys(newErrors).length === 0) {
                        setShowValidationErrors(false);
                      }
                    }
                  }}
                  name="birthMinute"
                  length={2}
                  disabled={infoCardData.unknownTime}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onBackspaceAtStart={() => {
                    const prev = document.querySelector("input[name='birthHour']");
                    if (prev) prev.focus();
                  }}
                />
              </div>
              {/* Example time format */}
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(139, 0, 0, 0.8)",
                  fontWeight: "bold",
                  marginTop: 2,
                  textAlign: "left",
                }}
              >
                Example: 04:00 PM
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getCompactSpacing().fieldGap,
                marginTop: 3,
                marginBottom: 3,
                marginLeft: 15,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  onClick={() => handleSetAmPm("AM")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    opacity: 1,
                  }}
                >
                  <CheckboxBox
                    checked={ampm === "AM" && !infoCardData.unknownTime}
                    onClick={() => handleSetAmPm("AM")}
                    disabled={false}
                    size={20}
                  />
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      color: "#8b0000",
                      cursor: "pointer",
                    }}
                  >
                    AM
                  </label>
                </div>
                <div
                  onClick={() => handleSetAmPm("PM")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    opacity: 1,
                  }}
                >
                  <CheckboxBox
                    checked={ampm === "PM" && !infoCardData.unknownTime}
                    onClick={() => handleSetAmPm("PM")}
                    disabled={false}
                    size={20}
                  />
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      color: "#8b0000",
                      cursor: "pointer",
                    }}
                  >
                    PM
                  </label>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <CheckboxBox
                  checked={infoCardData.unknownTime}
                  onClick={() => {
                    const newUnknownTime = !infoCardData.unknownTime;
                    handleChange({
                      target: {
                        name: "unknownTime",
                        type: "checkbox",
                        checked: newUnknownTime,
                      },
                    });
                    // Clear time fields when UNKNOWN is selected
                    if (newUnknownTime) {
                      setAmPm("AM");
                      setHour("");
                      setMinute("");
                    }
                  }}
                  disabled={false}
                  size={20}
                />
                <label
                  htmlFor="unknownTime"
                  style={{
                    fontWeight: "bold",
                    fontSize: 11,
                    textTransform: "uppercase",
                    color: "#8b0000",
                    cursor: "pointer",
                  }}
                >
                  UNKNOWN
                </label>
              </div>
            </div>
          </div>
          {showValidationErrors && validationErrors.time && (
            <div
              style={{
                color: "#dc3545",
                fontSize: 11,
                fontWeight: "bold",
                marginTop: 4,
                textAlign: "left",
              }}
            >
              ⚠️ {validationErrors.time}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          marginBottom: getCompactSpacing().fieldMargin,
          border: "2px solid #8b0000",
          padding: getCompactSpacing().fieldPadding,
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: 4,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: getCompactSpacing().fieldGap }}>
          <div
            style={{
              fontWeight: "bold",
              fontSize: getCompactSpacing().labelSize,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "#8b0000",
              textAlign: "left",
            }}
          >
            GENDER
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <CheckboxBox
                checked={infoCardData.gender === "F"}
                onClick={() =>
                  handleChange({
                    target: {
                      name: "gender",
                      value: "F",
                    },
                  })
                }
                disabled={false}
                size={20}
              />
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "#8b0000",
                  cursor: "pointer",
                }}
              >
                FEMALE
              </label>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <CheckboxBox
                checked={infoCardData.gender === "M"}
                onClick={() =>
                  handleChange({
                    target: {
                      name: "gender",
                      value: "M",
                    },
                  })
                }
                disabled={false}
                size={20}
              />
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "#8b0000",
                  cursor: "pointer",
                }}
              >
                MALE
              </label>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <CheckboxBox
                checked={infoCardData.gender === "O"}
                onClick={() =>
                  handleChange({
                    target: {
                      name: "gender",
                      value: "O",
                    },
                  })
                }
                disabled={false}
                size={20}
              />
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "#8b0000",
                  cursor: "pointer",
                }}
              >
                OTHER
              </label>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          marginBottom: getCompactSpacing().fieldMargin,
          border: `2px solid ${validationErrors.location ? "#dc3545" : "#8b0000"}`,
          padding: getCompactSpacing().fieldPadding,
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: 4,
        }}
        onClick={() => onShowCitySelector(updateCity)}
        data-field="location"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: getCompactSpacing().fieldGap }}>
          <div
            style={{
              fontWeight: "bold",
              fontSize: getCompactSpacing().labelSize,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: validationErrors.location ? "#dc3545" : "#8b0000",
              textAlign: "left",
            }}
          >
            PLACE OF BIRTH
          </div>
          <UnderlinedInput
            value={
              infoCardData.birthLocation ? infoCardData.birthLocation.name : ""
            }
            name="birthLocation"
            length={getResponsiveLength('birthLocation')}
            disabled={true}
          />
          {showValidationErrors && validationErrors.location && (
            <div
              style={{
                color: "#dc3545",
                fontSize: 11,
                fontWeight: "bold",
                marginTop: 4,
                textAlign: "left",
              }}
            >
              ⚠️ {validationErrors.location}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          marginTop: getCompactSpacing().buttonMargin,
          paddingTop: getCompactSpacing().buttonPadding,
          textAlign: "center",
        }}
      >
        <button
          style={{
            background: "linear-gradient(135deg, #f4f1e8 0%, #e8dcc0 100%)",
            color: "#8b0000",
            border: "2px solid #8b0000",
            padding: `${getCompactSpacing().buttonPadding}px 25px`,
            fontFamily: "Xanh Mono, monospace",
            fontSize: 13,
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: 1,
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
          onClick={handleContinue}
        >
          CONTINUE
        </button>
      </div>
    </div>
  );
};

export default forwardRef(InfoCard);
